import { useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { MultiSelect } from '@/components/ui/multi-select'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useStoreAttributeValues } from '@/hooks/useStoreAttributes'
import { cn } from '@/utils/cn'
import type { StoreModelLocationFilters } from '@/api/storeModels'

/** Store attributes exposed as location filters, in display order. */
const LOCATION_FIELDS = [
  { key: 'region', label: 'Region' },
  { key: 'district', label: 'District' },
  { key: 'state', label: 'State' },
] as const satisfies readonly {
  key: keyof StoreModelLocationFilters
  label: string
}[]

interface LocationFieldSelectProps {
  field: keyof StoreModelLocationFilters
  label: string
  value: string[]
  onChange: (value: string[]) => void
}

/**
 * One attribute's value picker. Values are fetched per field, so this is a
 * component rather than a loop body — hooks cannot be called in a loop whose
 * length could change.
 */
function LocationFieldSelect({
  field,
  label,
  value,
  onChange,
}: LocationFieldSelectProps) {
  const { data, isLoading } = useStoreAttributeValues(field)

  const options = (data?.values ?? []).map(v => ({ value: v, label: v }))

  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1 block">
        {label}
      </label>
      <MultiSelect
        options={options}
        value={value}
        onValueChange={onChange}
        placeholder={isLoading ? 'Loading...' : `All ${label}s`}
        isLoading={isLoading}
        containScroll
        className="w-full"
      />
    </div>
  )
}

interface StoreModelsLocationFilterProps {
  value: StoreModelLocationFilters
  onValueChange: (value: StoreModelLocationFilters) => void
  className?: string
}

export function StoreModelsLocationFilter({
  value,
  onValueChange,
  className,
}: StoreModelsLocationFilterProps) {
  const [open, setOpen] = useState(false)

  const selectedCount = LOCATION_FIELDS.reduce(
    (total, { key }) => total + (value[key]?.length ?? 0),
    0
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-10 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer whitespace-nowrap',
            className
          )}
        >
          {/* Reads like the neighbouring "All Types" / "All Statuses" selects
              when nothing is picked. */}
          <span className="truncate text-foreground">
            {selectedCount === 0 ? 'All Locations' : 'Locations'}
          </span>
          {selectedCount > 0 && (
            <Badge variant="info" className="ml-2">
              {selectedCount}
            </Badge>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <div className="flex flex-col gap-3">
          {LOCATION_FIELDS.map(({ key, label }) => (
            <LocationFieldSelect
              key={key}
              field={key}
              label={label}
              value={value[key] ?? []}
              onChange={values => onValueChange({ ...value, [key]: values })}
            />
          ))}

          <p className="text-xs text-muted-foreground">
            Shows models whose location expression matches every selection.
          </p>

          {selectedCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 cursor-pointer self-start"
              onClick={() => onValueChange({})}
            >
              <X className="h-3.5 w-3.5" />
              Clear locations
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
